import { ApiRoutes } from "@/lib/routeResolver";
import { shuffleArray, normalizeName } from "@/lib/helpers";
import { cache } from "react";
import api from "./api";
import { fetchCruiseRegionsMenu, type CruiseRegionMenu } from "./cruises";

// ─── Type Definitions ────────────────────────────────────────────────

/** Per-brand contact override block returned inside `contactBrandDetails[]`. */
export interface ContactBrandDetail {
  brandId?: string;
  brandName?: string;
  email1?: string;
  email2?: string;
  mainPhone?: string;
  localPhone?: string;
  fax?: string;
}

export interface ContactInfo {
  mainPhone?: string;
  localPhone?: string;
  fax?: string;
  email1?: string;
  email2?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  contactBrandDetails?: ContactBrandDetail[];
  [key: string]: unknown;
}

const MAIN_BRAND_ID = process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS;

/**
 * Merge the brand-specific contact block (from `contactBrandDetails[]`, matched
 * by `brandId`) over the top-level contact record. Brand values win only when
 * non-empty, and every value is trimmed (the API occasionally returns a leading
 * space on emails). Returns the record unchanged when there's no brand match.
 * Defaults to the Central Holidays brand id.
 */
export function resolveBrandContact(
  record: ContactInfo | null | undefined,
  brandId: string | undefined = MAIN_BRAND_ID,
): ContactInfo {
  const base: ContactInfo = record ?? {};
  const details = Array.isArray(base.contactBrandDetails)
    ? base.contactBrandDetails
    : [];
  const brand = brandId
    ? details.find((b) => b?.brandId === brandId)
    : undefined;

  const clean = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim() : undefined;

  return {
    ...base,
    email1: clean(brand?.email1) ?? clean(base.email1),
    email2: clean(brand?.email2) ?? clean(base.email2),
    mainPhone: clean(brand?.mainPhone) ?? clean(base.mainPhone),
    localPhone: clean(brand?.localPhone) ?? clean(base.localPhone),
    fax: clean(brand?.fax) ?? clean(base.fax),
  };
}

export interface SocialMediaLink {
  id: string;
  socialMediaURL: string;
  iconURL: string;
  isActive: boolean;
  order: number;
  [key: string]: unknown;
}

export interface Destination {
  id: string;
  destinationId: string;
  pageURL: string;
  isNavShown: boolean;
  [key: string]: unknown;
}

export interface Region {
  id: string;
  name: string;
  regionURL?: string;
  countriesId: string[];
  countriesNames: string[];
  countriesURLs: string[];
  packagesImages?: string[];
  [key: string]: unknown;
}

export interface WebsiteRegion {
  regionName?: string;
  coverPhoto?: string;
  randomCoverPhoto?: string;
  description?: string;
  destinationForRegions?: {
    destinationId: string;
    coverPhoto?: string;
    [key: string]: unknown;
  }[];
  pageSEO?: {
    pageURL?: string;
    pageTitle?: string;
    pageDescription?: string;
    pageKeywords?: string;
    head?: string;
  };
  pageOG?: { propertyName: string; propertyValue: string }[];
  [key: string]: unknown;
}

export interface PageModule {
  id: string;
  name: string;
  isActive: boolean;
  isHeaderPage: boolean;
  isFooterPage: boolean;
  headerOrder: number;
  order: number;
  isExternalLink?: boolean;
  externalLink?: string;
  [key: string]: unknown;
}

export interface PageContainer {
  id: string;
  pageModuleId: string;
  pageSubModuleId: string;
  isExtrnalLink: boolean;
  externalLinkUrl?: string;
  isPdfUrl: boolean;
  pdfUrl?: string;
  target?: string;
  pageSEO?: {
    pageURL?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface Company {
  logo?: string;
  [key: string]: unknown;
}

export interface ExperienceRegion {
  regionId: string;
  regionName: string;
  regionPageURL: string;
  randomCoverPhoto: string | null;
  destinations?: {
    calculatedCount?: number;
    destinationId: string;
    destinationName: string;
    destinationPageURL: string;
  }[];
}

export interface CruiseType {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface PageContent {
  title: string;
  subTitle: string;
  headerPhoto: string;
  description: string;
}

export interface SectionPackage {
  id: string;
  name: string;
  image: string;
  countriesNames: string[];
  startingFrom: string;
  pageURL: string;
  days: number;
}

export interface PageSection {
  title: string;
  subTitle: string;
  layout: string;
  photo: string | null;
  body: string;
  order: number;
  isActive: boolean;
  firstButtonText: string;
  firstButtonLink: string;
  firstButtonLinkType: string;
  secondButtonText: string;
  secondButtonLink: string;
  secondButtonLinkType: string;
  isPackageSection: boolean;
  packageIds: string[];
  packages: SectionPackage[] | null;
  smallImage: boolean;
}

export interface PageContainerDetail {
  id: string;
  pageContent: PageContent;
  features: string[];
  sections: PageSection[];
  pageSEO: {
    pageURL: string;
    pageTitle: string;
    pageDescription: string;
    pageKeywords: string;
    head: string;
  };
  isActive: boolean;
  [key: string]: unknown;
}

export interface SubPage {
  id: string;
  title: string;
  isActive: boolean;
  order: number;
  pageContainers: PageContainerDetail[];
}

export interface PageModuleWithSubs {
  id: string;
  name: string;
  isActive: boolean;
  pageContainers: PageContainerDetail[];
  subPages: SubPage[];
}

export interface FooterData {
  companyName?: string;
  description?: string;
  logoURL?: string;
  [key: string]: unknown;
}

export interface NavigationData {
  company: Company;
  pageData: PageModule[];
  pagecontainerData: PageContainer[];
  destination: Destination[];
  reginData: Region[];
  contactus: ContactInfo;
  socialMedia: SocialMediaLink[];
  resRegionsWithToursData: ExperienceRegion[];
  usedCruiseTypesData: CruiseType[];
  cruiseRegionsData: CruiseRegionMenu[];
}

// ─── Data Fetching ───────────────────────────────────────────────────

const companyId = process.env.NEXT_PUBLIC_COMPANY_ID;

/**
 * Fetch all navigation data. Cached per-request via React `cache()`.
 */
export const fetchNavigation = cache(async (): Promise<NavigationData> => {
  const safeFetch = <T>(promise: Promise<T>, label: string) =>
    promise.catch((err) => {
      console.error(`[Navigation] Failed to fetch ${label}:`, err?.message);
      return null;
    });

  const [
    resCompany,
    resPage,
    resCon,
    resDest,
    resRegion,
    resContact,
    resSocial,
    resRegionsWithTours,
    resUsedCruiseTypes,
    cruiseRegionsData,
  ] = await Promise.all([
    safeFetch(api.get(ApiRoutes.ADMIN_COMPANY_CACHING(companyId)), "company"),
    safeFetch(
      api.get(ApiRoutes.WEBSITE_PAGEMODULE_CACHING, {
        params: { PageSize: 1000, CompanyId: companyId },
      }),
      "pagemodule",
    ),
    safeFetch(
      api.get(ApiRoutes.WEBSITE_PAGECONTAINER_MINI_CACHING, {
        params: { PageSize: 1000, CompanyId: companyId },
      }),
      "pagecontainer",
    ),
    safeFetch(
      api.get(ApiRoutes.WEBSITE_DESTINATION_MINI_CACHING, {
        params: { PageSize: 1000, CompanyId: companyId },
      }),
      "destination",
    ),
    safeFetch(
      api.get(ApiRoutes.FOUNDATION_REGION_WITH_PACKAGES(companyId)),
      "region",
    ),
    safeFetch(
      api.get(ApiRoutes.WEBSITE_CONTACTUS_CACHING, {
        params: { CompanyId: companyId },
      }),
      "contactus",
    ),
    safeFetch(
      api.get(ApiRoutes.WEBSITE_SOCIALMEDIALINK_CACHING, {
        params: { PageSize: 1000, PageNumber: 1, CompanyId: companyId },
      }),
      "socialmedia",
    ),
    safeFetch(api.get(ApiRoutes.INVENTORY_TOUR_EXPERIENCE), "tour-experience"),
    safeFetch(
      api.get(ApiRoutes.INVENTORY_PACKAGE_USED_CRUISE_TYPES),
      "cruise-types",
    ),
    fetchCruiseRegionsMenu(),
  ]);

  const pageData: PageModule[] = (resPage?.data?.data ?? [])
    .filter((p: PageModule) => p.isActive)
    .sort((a: PageModule, b: PageModule) => a.headerOrder - b.headerOrder);

  return {
    company: resCompany?.data?.data ?? {},
    pageData,
    pagecontainerData: resCon?.data?.data ?? [],
    destination: resDest?.data?.data ?? [],
    reginData: resRegion?.data?.data ?? [],
    contactus: resolveBrandContact(resContact?.data?.data),
    socialMedia: resSocial?.data?.data ?? [],
    resRegionsWithToursData: resRegionsWithTours?.data ?? [],
    usedCruiseTypesData: resUsedCruiseTypes?.data ?? [],
    cruiseRegionsData: cruiseRegionsData ?? [],
  };
});

/** A country used somewhere in the system, as `{ id, name }`. */
export interface SystemCountry {
  id: string;
  name: string;
}

/**
 * Every country used in the system, flattened from the region → countries data,
 * de-duplicated by id and sorted alphabetically by name. Backs country filter
 * widgets and name→id resolution (see the infographics page). Reuses the cached
 * `fetchNavigation` payload, so it adds no extra request.
 */
export const fetchSystemCountries = cache(
  async (): Promise<SystemCountry[]> => {
    const nav = await fetchNavigation();
    const byId = new Map<string, string>();
    for (const region of nav.reginData) {
      region.countriesId?.forEach((id, i) => {
        const name = region.countriesNames?.[i];
        if (id && name && !byId.has(id)) byId.set(id, name);
      });
    }
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  },
);

/**
 * Resolve a country name (e.g. from a ?country URL param) to its id against a
 * `fetchSystemCountries()` list, by normalized comparison — case- and
 * whitespace-insensitive — returning the first match. Empty string when there's
 * no name or no match, which callers treat as "no destination filter".
 */
export function resolveSystemCountryId(
  countries: SystemCountry[],
  countryName: string | undefined,
): string {
  const target = normalizeName(countryName);
  if (!target) return "";
  return countries.find((c) => normalizeName(c.name) === target)?.id ?? "";
}

/**
 * Fetch footer-specific data (company description, logo, etc.)
 */
export const fetchFooterData = cache(async (): Promise<FooterData> => {
  const res = await api.get(ApiRoutes.WEBSITE_FOOTER_CACHING);
  return res?.data?.data ?? {};
});

// ─── Home Destinations ───────────────────────────────────────────────

export interface HomeDestination {
  id: string;
  destinationId: string;
  pageURL: string;
  coverPhoto: string;
  countryName: string;
}

/**
 * Fetch destinations marked as home-shown, enriched with country name from regions.
 * Reuses the cached fetchNavigation() result — no extra API calls.
 */
export const fetchHomeDestinations = cache(
  async (): Promise<HomeDestination[]> => {
    const nav = await fetchNavigation();

    const homeDestinations = (
      nav.destination as Record<string, unknown>[]
    ).filter((d) => d.isHomeShown && d.coverPhoto);

    return homeDestinations.map((dest) => {
      let countryName = "";
      for (const region of nav.reginData) {
        const idx = region.countriesId.indexOf(dest.destinationId as string);
        if (idx !== -1) {
          countryName = region.countriesNames[idx];
          break;
        }
      }
      return {
        id: dest.id as string,
        destinationId: dest.destinationId as string,
        pageURL: (dest.pageURL as string) ?? "",
        coverPhoto: (dest.coverPhoto as string) ?? "",
        countryName,
      };
    });
  },
);

/**
 * Build a destinationId -> countryName map from ALL destinations + regions.
 * Reuses the cached fetchNavigation() result — no extra API calls.
 */
export const fetchDestinationsMap = cache(
  async (): Promise<Record<string, string>> => {
    const nav = await fetchNavigation();
    const map: Record<string, string> = {};

    for (const dest of nav.destination as Record<string, unknown>[]) {
      const destId = dest.destinationId as string;
      if (!destId) continue;

      for (const region of nav.reginData) {
        const idx = region.countriesId.indexOf(destId);
        if (idx !== -1) {
          map[destId] = region.countriesNames[idx];
          break;
        }
      }
    }

    return map;
  },
);

// ─── Home Packages ───────────────────────────────────────────────────

export interface HomePackage {
  id: string;
  pageURL: string;
  packageName: string;
  photoURL: string;
  startingPrice: number;
  cities: string[];
  days: number;
  nights: number;
  // Nights aboard a cruise; 0 for land-only packages. Drives the card's
  // "Cruise" chip.
  cruiseNights: number;
  packageTypeName: string;
  isNew?: boolean;
  // Whether the package has an active offer — drives the card's "offer" badge.
  hasOffers?: boolean;
  packageThemesNames?: string[];
}

/** A package row as returned by `inventory/package/forwebsite`. */
export interface WebsitePackage {
  id: string;
  pageURL: string;
  packageName: string;
  photoURL: string;
  startingPrice: number;
  cities: string[];
  days: number;
  nights: number;
  cruiseNights: number;
  packageTypeName: string;
  packageThemesIds: string[];
  packageThemesNames: string[];
  websiteOrder: number;
  isNew: boolean;
  hasOffers: boolean;
}

/** Narrow a `forwebsite` row to what a package card renders. */
export function mapWebsitePackage(pack: WebsitePackage): HomePackage {
  return {
    id: pack.id,
    pageURL: pack.pageURL,
    packageName: pack.packageName,
    photoURL: pack.photoURL ?? "",
    startingPrice: Number(pack.startingPrice ?? 0),
    cities: (pack.cities ?? []).filter(Boolean),
    days: pack.days ?? 0,
    nights: pack.nights ?? 0,
    cruiseNights: pack.cruiseNights ?? 0,
    packageTypeName: pack.packageTypeName ?? "",
    isNew: pack.isNew === true,
    hasOffers: pack.hasOffers === true,
    packageThemesNames: pack.packageThemesNames ?? [],
  };
}

/**
 * Fetch packages marked for the home page (isHomePage === true),
 * deduplicated by country — mirrors the old page's packageShow logic.
 */
export const fetchHomePackages = cache(async (): Promise<HomePackage[]> => {
  const res = await api.get(ApiRoutes.INVENTORY_PACKAGE, {
    params: { PageSize: 1000, IsWebsite: true, BrandId: MAIN_BRAND_ID },
  });

  const allPackages: Record<string, unknown>[] = res?.data?.data ?? [];

  const seenCountries = new Set<string>();
  const homePackages: Record<string, unknown>[] = [];

  for (const pack of allPackages) {
    const regions = (pack.packageRegions as { countryId?: string }[]) ?? [];
    const isHomePage = pack.homePageShown === true;
    const countryId = regions[0]?.countryId;

    if (isHomePage && countryId) {
      seenCountries.add(countryId);
      homePackages.push(pack);
    }
  }

  return homePackages.map((pack) => {
    const gallery = (pack.photoGallery as { url: string }[] | null) ?? [];
    const regions =
      (pack.packageRegions as { cityDays?: { cityName?: string }[] }[]) ?? [];
    const rate = (pack.rate as { startingFrom?: string | number } | null) ?? {};

    const cities = regions
      .flatMap((r) => r.cityDays ?? [])
      .map((cd) => cd.cityName ?? "")
      .filter(Boolean);

    return {
      id: pack.id as string,
      pageURL: (pack.pageURL as string) ?? "",
      packageName: (pack.packageName as string) ?? "",
      photoURL: gallery[0]?.url ?? "",
      startingPrice: Number(rate.startingFrom ?? 0),
      cities,
      days: (pack.days as number) ?? 0,
      nights: (pack.nights as number) ?? 0,
      cruiseNights: (pack.cruiseNights as number) ?? 0,
      packageTypeName: (pack.packageTypeName as string) ?? "",
      isNew: pack.isNew === true,
    };
  });
});

// ─── Home Testimonials ───────────────────────────────────────────────

export interface HomeTestimonial {
  id: string;
  name: string;
  createdDate: string;
  photoURL: string;
  comment: string;
}

/**
 * Fetch accepted testimonials marked for the home page.
 */
export const fetchHomeTestimonials = cache(
  async (): Promise<HomeTestimonial[]> => {
    const res = await api.get(ApiRoutes.WEBSITE_TESTIMONIAL_MINI, {
      params: {
        PageSize: 1000,
        PageNumber: 1,
        Status: "accepted",
        IsActive: true,
        IsHomeShown: true,
      },
    });

    const items: Record<string, unknown>[] = res?.data?.data ?? [];

    return items.map((t) => ({
      id: t.id as string,
      name: (t.signature as string) ?? "",
      createdDate: (t.createdDate as string) ?? "",
      photoURL: (t.photo as string) ?? "",
      comment: (t.description as string) ?? "",
    }));
  },
);

// ─── Home Articles ───────────────────────────────────────────────────

export interface HomeArticle {
  id: string;
  title: string;
  createdDate: string;
  photoURL: string;
  pageURL: string;
}

/**
 * Fetch articles marked for the home page (IsHomeShown === true).
 */
export const fetchHomeArticles = cache(async (): Promise<HomeArticle[]> => {
  const res = await api.get(ApiRoutes.WEBSITE_ARTICLE_MINI, {
    params: { PageSize: 6, PageNumber: 1, IsHomeShown: true },
  });

  const articles: Record<string, unknown>[] = res?.data?.data ?? [];

  return articles.map((art) => {
    const pageContent = (art.pageContent as Record<string, unknown>) ?? {};
    const pageSEO = (art.pageSEO as Record<string, unknown>) ?? {};
    return {
      id: art.id as string,
      title: (pageContent.title as string) ?? "",
      createdDate: (art.createdDate as string) ?? "",
      photoURL: (pageContent.headerPhoto as string) ?? "",
      pageURL: (pageSEO.pageURL as string) ?? "",
    };
  });
});

// ─── Home Page Sections ──────────────────────────────────────────────

/**
 * Private cached fetcher for the Central Holidays homepage sections payload.
 * Every section helper below reads from it, so the upstream request fires only
 * once per request instead of once per section. Returns `null` on any failure
 * so each helper can fall back to its own static copy.
 */
const fetchHomeSectionsRaw = cache(
  async (): Promise<Record<string, unknown> | null> => {
    try {
      const res = await api.get(ApiRoutes.WEBSITE_HOMEPAGESECTIONS, {
        params: { CompanyId: companyId, BrandId: MAIN_BRAND_ID },
      });
      const sections: Record<string, unknown>[] = res?.data?.data ?? [];
      return sections[0] ?? null;
    } catch {
      return null;
    }
  },
);

// ─── Home About ──────────────────────────────────────────────────────

export interface HomeAbout {
  image: string;
  subTitle: string;
  title: string;
  description: string;
}

/**
 * Fetch the About Us section data for the home page.
 * Reads `aboutUsData` from the homepage sections payload.
 */
export const fetchHomeAbout = cache(async (): Promise<HomeAbout | null> => {
  const aboutUsData = (await fetchHomeSectionsRaw())?.aboutUsData as
    | Record<string, unknown>
    | undefined;

  if (!aboutUsData) return null;

  return {
    image: (aboutUsData.image as string) || "",
    subTitle: (aboutUsData.subTitle as string) ?? "",
    title: (aboutUsData.title as string) ?? "",
    description: (aboutUsData.description as string) ?? "",
  };
});

// ─── Home Gallery ────────────────────────────────────────────────────

export interface GalleryPhoto {
  url: string;
  title: string;
  pageURL: string;
}

/**
 * Fetch 7 random package photos for a gallery section.
 * Each package contributes up to 2 photos from its photoGallery.
 * Pass `countryId` to scope the photos to a single destination (used by the
 * destination page); omit it for the site-wide home gallery.
 */
export const fetchGalleryPhotos = cache(
  async (countryId?: string): Promise<GalleryPhoto[]> => {
    const res = await api.get(ApiRoutes.INVENTORY_PACKAGE_RANDOM_PHOTOS, {
      params: countryId ? { CountryId: countryId } : undefined,
    });
    const packages: Record<string, unknown>[] = res?.data?.data ?? [];

    const photos: GalleryPhoto[] = [];
    for (const pack of packages) {
      const gallery =
        (pack.photoGallery as { url?: string; title?: string }[]) ?? [];
      const pageURL = (pack.pageURL as string) ?? "";
      for (const photo of gallery) {
        if (photo.url) {
          photos.push({
            url: photo.url,
            title: photo.title ?? "",
            pageURL,
          });
        }
        if (photos.length >= 7) break;
      }
      if (photos.length >= 7) break;
    }

    return photos;
  },
);

// ─── Experience Tours ────────────────────────────────────────────────

export interface ExperienceTour {
  id: string;
  name: string;
  destinationName: string;
  destinationSlug: string;
  photoURL: string;
}

/**
 * Fetch 10 website-facing tours, shuffle them, and return 5 random ones
 * for the home page Experience slider.
 */
export const fetchExperienceTours = cache(
  async (): Promise<ExperienceTour[]> => {
    const res = await api.get(ApiRoutes.INVENTORY_TOUR, {
      params: { PageNumber: 1, PageSize: 30, IsWebsite: true },
    });

    const allTours: Record<string, unknown>[] = res?.data?.data ?? [];

    const toursWithImages = allTours.filter((tour) => {
      const photos = (tour.photos as Record<string, unknown>) ?? {};
      const gallery = (tour.photoGallery as { url?: string }[]) ?? [];
      return (photos.url as string) || gallery[0]?.url;
    });

    const selected = shuffleArray(toursWithImages).slice(0, 8);

    return selected.map((tour) => {
      const region = (tour.region as Record<string, unknown>) ?? {};
      const photos = (tour.photos as Record<string, unknown>) ?? {};
      const gallery = (tour.photoGallery as { url?: string }[]) ?? [];
      const destinationName = (region.destinationName as string) ?? "";

      return {
        id: tour.id as string,
        name: (tour.name as string) ?? "",
        destinationName,
        destinationSlug: destinationName.replace(/\s+/g, "-"),
        photoURL: (photos.url as string) || (gallery[0]?.url ?? "") || "",
      };
    });
  },
);

// ─── Experience Destinations ─────────────────────────────────────────

export interface ExperienceDestinationCard {
  id: string;
  name: string;
  slug: string;
  regionName: string;
  regionSlug: string;
  coverPhoto: string;
  tourCount: number;
}

/**
 * Every destination that has experience tours, flattened out of its region.
 * Reuses the cached fetchNavigation() result — no extra API calls.
 */
export const fetchExperienceDestinations = cache(
  async (): Promise<ExperienceDestinationCard[]> => {
    const nav = await fetchNavigation();

    const photoByDestination = new Map(
      (nav.destination as Record<string, unknown>[]).map((d) => [
        d.destinationId as string,
        (d.coverPhoto as string) ?? "",
      ]),
    );

    return nav.resRegionsWithToursData.flatMap((region) =>
      (region.destinations ?? []).map((dest) => ({
        id: dest.destinationId,
        name: dest.destinationName,
        slug:
          dest.destinationPageURL ||
          dest.destinationName.replace(/\s+/g, "-"),
        regionName: region.regionName,
        regionSlug: region.regionPageURL,
        coverPhoto:
          photoByDestination.get(dest.destinationId) ||
          region.randomCoverPhoto ||
          "",
        tourCount: dest.calculatedCount ?? 0,
      })),
    );
  },
);

// ─── Home Page Data ─────────────────────────────────────────────────

export interface HomeSlider {
  id: string;
  title: string;
  subTitle?: string;
  photoURL: string;
  packageId?: string;
  isActive: boolean;
  order: number;
  [key: string]: unknown;
}

/**
 * Fetch homepage sliders (hero section). Cached per-request.
 */
export const fetchHomeSliders = cache(async (): Promise<HomeSlider[]> => {
  const res = await api.get(ApiRoutes.WEBSITE_HOMEPAGESLIDER);
  const sliders: HomeSlider[] = res?.data?.data ?? [];
  return sliders.filter((s) => s.isActive).sort((a, b) => a.order - b.order);
});

// ─── Curvy Slider ──────────────────────────────────────────────────

export interface CurvySliderItem {
  slideId: string;
  title: string;
  url: string;
  photo: string;
  regionId: string | null;
  destinationId: string | null;
  packageId: string | null;
  tourId: string | null;
  pageContainerId: string | null;
  cruiseTypeId: string | null;
  isAbsoluteUrl: boolean;
  // Anchor `target` for absolute links. Null → open in a new tab (`_blank`);
  // otherwise use the value verbatim (e.g. `_self` to open in the same tab).
  target: string | null;
  order: number;
}

interface CurvySliderGroup {
  id: string;
  sliderItems?: CurvySliderItem[];
}

/**
 * Fetch curvy slider items for the homepage. Cached per-request.
 * The API returns groups, each containing a list of slider items — we flatten
 * them and sort by order.
 */
export const fetchCurvySlider = cache(async (): Promise<CurvySliderItem[]> => {
  const res = await api.get(ApiRoutes.WEBPACK_CURVY_SLIDER, {
    params: { CompanyId: companyId, PageNumber: 1, PageSize: 100 },
  });
  const groups: CurvySliderGroup[] = res?.data?.data ?? [];
  const items = groups
    .flatMap((g) => g.sliderItems ?? [])
    // The API occasionally includes a blank slide (no image and no title); it
    // would render as an empty card showing only the placeholder photo, so drop
    // any item that has neither an image nor a title.
    .filter((item) => Boolean(item && (item.photo?.trim() || item.title?.trim())));
  return items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
});

/** Header copy for the curvy slider section. Empty fields mean "use the default". */
export interface CurvySliderSection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the curvy slider section header (`curvySliderData`) from the homepage
 * sections payload. Always returns a record — empty fields let the consumer
 * apply its own static copy. Cached per-request.
 */
export const fetchCurvySliderSection = cache(
  async (): Promise<CurvySliderSection> => {
    const section = (await fetchHomeSectionsRaw())?.curvySliderData as
      | Record<string, unknown>
      | undefined;

    return {
      title: ((section?.title as string) ?? "").trim(),
      subTitle: ((section?.subTitle as string) ?? "").trim(),
    };
  },
);

// ─── Top Destinations ───────────────────────────────────────────────

/** Header copy for the top destinations section. Empty fields mean "use the default". */
export interface TopDestinationSection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the top destinations section header (`topDestinationData`) from the
 * homepage sections payload. Always returns a record — empty fields let the
 * consumer apply its own static copy. Cached per-request.
 */
export const fetchTopDestinationSection = cache(
  async (): Promise<TopDestinationSection> => {
    const section = (await fetchHomeSectionsRaw())?.topDestinationData as
      | Record<string, unknown>
      | undefined;

    return {
      title: ((section?.title as string) ?? "").trim(),
      subTitle: ((section?.subTitle as string) ?? "").trim(),
    };
  },
);

// ─── Popular Packages ───────────────────────────────────────────────

/** Header copy for the popular packages section. Empty fields mean "use the default". */
export interface PopularPackageSection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the popular packages section header (`popularPackageData`) from the
 * homepage sections payload. Always returns a record — empty fields let the
 * consumer apply its own static copy. Cached per-request.
 */
export const fetchPopularPackageSection = cache(
  async (): Promise<PopularPackageSection> => {
    const section = (await fetchHomeSectionsRaw())?.popularPackageData as
      | Record<string, unknown>
      | undefined;

    return {
      title: ((section?.title as string) ?? "").trim(),
      subTitle: ((section?.subTitle as string) ?? "").trim(),
    };
  },
);

// ─── Testimonials ───────────────────────────────────────────────────

/** Header copy for the testimonials section. Empty fields mean "use the default". */
export interface TestimonialSection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the testimonials section header (`testimonialData`) from the homepage
 * sections payload. Always returns a record — empty fields let the consumer
 * apply its own static copy. Cached per-request.
 */
export const fetchTestimonialSection = cache(
  async (): Promise<TestimonialSection> => {
    const section = (await fetchHomeSectionsRaw())?.testimonialData as
      | Record<string, unknown>
      | undefined;

    return {
      title: ((section?.title as string) ?? "").trim(),
      subTitle: ((section?.subTitle as string) ?? "").trim(),
    };
  },
);

// ─── Blog ───────────────────────────────────────────────────────────

/** Header copy for the blog section. Empty fields mean "use the default". */
export interface BlogSection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the blog section header (`blogData`) from the homepage sections
 * payload. Always returns a record — empty fields let the consumer apply its
 * own static copy. Cached per-request.
 */
export const fetchBlogSection = cache(async (): Promise<BlogSection> => {
  const section = (await fetchHomeSectionsRaw())?.blogData as
    | Record<string, unknown>
    | undefined;

  return {
    title: ((section?.title as string) ?? "").trim(),
    subTitle: ((section?.subTitle as string) ?? "").trim(),
  };
});

// ─── Gallery ────────────────────────────────────────────────────────

/** Header copy for the gallery section. Empty fields mean "use the default". */
export interface GallerySection {
  title: string;
  subTitle: string;
}

/**
 * Fetch the gallery section header (`galleryData`) from the homepage sections
 * payload. Always returns a record — empty fields let the consumer apply its
 * own static copy. Cached per-request.
 */
export const fetchGallerySection = cache(async (): Promise<GallerySection> => {
  const section = (await fetchHomeSectionsRaw())?.galleryData as
    | Record<string, unknown>
    | undefined;

  return {
    title: ((section?.mainTitle as string) ?? "").trim(),
    subTitle: ((section?.subTitle as string) ?? "").trim(),
  };
});

// ─── Page Container ─────────────────────────────────────────────────

/**
 * Fetch page container data by page URL slug.
 * Returns the first matching page container or null.
 */
export const fetchPageContainer = cache(
  async (pageUrl: string): Promise<PageContainerDetail | null> => {
    const res = await api.get(ApiRoutes.WEBSITE_PAGECONTAINER, {
      params: { pageUrl },
    });
    const items: PageContainerDetail[] = res?.data?.data ?? [];
    return items[0] ?? null;
  },
);

/**
 * Fetch the Terms & Conditions body from the dedicated `admin/terms` endpoint.
 * Unlike other dynamic pages — whose body is the CMS page-container content —
 * the Terms page sources only its body here (the page container still provides
 * the header, banner and SEO). Scoped to the Central Holidays brand; returns
 * an empty string on any failure so the page degrades instead of throwing.
 */
export const fetchTermsContent = cache(async (): Promise<string> => {
  try {
    const res = await api.get(ApiRoutes.ADMIN_TERMS, {
      params: { CompanyId: companyId, BrandId: MAIN_BRAND_ID },
    });
    return (res?.data?.data?.[0]?.termsandConditions as string) || "";
  } catch {
    return "";
  }
});

/**
 * Fetch all page modules with their sub-pages and page containers.
 * Cached per-request via React `cache()`.
 */
export const fetchPagesWithSubPages = cache(
  async (): Promise<PageModuleWithSubs[]> => {
    const res = await api.get(ApiRoutes.WEBSITE_PAGEMODULE_CONTENT, {
      params: { companyId },
    });
    return res?.data?.data?.pages ?? [];
  },
);

/**
 * Find a page module by matching its pageContainer's pageSEO.pageURL to the given slug.
 */
export const findPageModuleBySlug = cache(
  async (slug: string): Promise<PageModuleWithSubs | null> => {
    const pages = await fetchPagesWithSubPages();
    return (
      pages.find((page) =>
        page.pageContainers.some((pc) => pc.pageSEO?.pageURL === slug),
      ) ?? null
    );
  },
);
